import styled from "styled-components"
import Modal from "./Modal"
import IcClose from "../assets/images/bg_close.svg"
import PlayerCardHolder from "./PlayerCardHolder"
import { selectionBatterPositionKeys } from "../utils/publicKeys"
import PlayerCard from "./PlayerCard"
import { useSquad } from "../contexts/SquadContext"
import DraggablePlayerCard from "./DraggablePlayerCard"
import DroppablePlayerCardHolder from "./DroppablePlayerCardHolder"
import { useState } from "react"
import { 
    closestCenter, 
    DndContext, 
    useSensors, 
    useSensor, 
    PointerSensor, // 포인터/마우스 센서
    TouchSensor      // 터치 센서 (모바일 환경 개선을 위해 추가)
} from "@dnd-kit/core"


const ModalContainer = styled.div`
    background-color: white;
    width: 70.5rem;
    height: 46.25rem;
`

const ModalHeader = styled.div`
    position: relative;
    height: 5.75rem;
    justify-content: center;
    align-items: center;
    display: flex;
    font-family: 'Esamanru', sans-serif;
    font-size: 1.5rem;
    font-weight: 200;
    color: #545454;
    border-bottom: 0.125rem solid #545454;
`

const CloseButton = styled.img`
    position: absolute;
    right: 1.625rem;
    width: 2.5rem;
    height: 2.5rem;
    cursor: pointer;
`

const HorizontalConatiner = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2rem;
`

const VerticalContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
`

const LineupHeader = styled.div`
    width: 100%;
    padding-top: 0.21875rem;
    padding-bottom: 0.21875rem;
    color: white;
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    text-align: center;
`

const RedLineupHeader = styled(LineupHeader)`
    background-color: #FF3131;
`

const BlueLineupHeader = styled(LineupHeader)`
    background-color: #316BFF;
`

const GreenLineupHeader = styled(LineupHeader)`
    background-color: #08A900;
`

const BatterLineupConatiner = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 1.625rem;
    justify-content: center;
    align-items: center;
`

const CandidateContainer = styled.div`
    height: 9.0625rem;
    display: flex;
    flex-direction: row;
    gap: 1rem;
`

const ContentContainer = styled.div`
    height: 40.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3.125rem;
`

const upperLineup = ['1번', '2번'];
const cleanupLineup = ['3번', '4번', '5번'];
const lowerLineup = ['6번', '7번', '8번', '9번'];

export default function BatterLineUpModal({isModalOpen, closeModal}) {

    const { playerInfos, setPlayers } = useSquad()
    const [activeItem, setActiveItem] = useState(null);

    // 모바일 환경 개선을 위한 센서 설정
    const sensors = useSensors(
        useSensor(PointerSensor), // 마우스 및 기타 포인터 장치
        useSensor(TouchSensor, {  // 터치 장치 (모바일)
            activationConstraint: {
                delay: 0, // 250ms 동안 누르고 있어야 드래그로 인식 시작
                tolerance: 1000, // 5px 이내의 움직임은 무시
            },
        })
    );


    const handleDragStart = (event) => {
        setActiveItem(event.active.data.current.playerData);
    };

    const handleDragEnd = (event) => {
        setActiveItem(null); 
        
        const { active, over } = event;
        if (!over) return;

        const draggedPlayer = active.data.current.playerData;
        const sourceId = active.id;                             
        const targetId = over.id;                              

        if (targetId.startsWith('slot-')) {
            const targetOrder = over.data.current.order;

            const newLineup = { ...playerInfos };

            // 소스 위치에서 선수 제거 (슬롯에서 드래그했으면)
            if (sourceId.startsWith('slot-')) {
                const oldOrder = active.data.current.order;
                newLineup[oldOrder] = null;
            }

            const existingPlayerInTarget = playerInfos[targetOrder];
            
            // 소스가 슬롯이었으면 (스왑)
            if (sourceId.startsWith('slot-')) {
                newLineup[targetOrder] = draggedPlayer; 
                const oldOrder = active.data.current.order;
                newLineup[oldOrder] = existingPlayerInTarget; // 기존 선수를 원래 위치로 이동 (스왑)
            
            // 소스가 후보 선수였으면 (배치)
            } else {
                newLineup[targetOrder] = draggedPlayer;
            }

            setPlayers(newLineup);
        }
    };

    return (
        isModalOpen && <Modal
            isOpen={isModalOpen} onClose={closeModal}
        >
            <ModalContainer>
                <DndContext
                    sensors={sensors} // 개선된 센서 적용
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    collisionDetection={closestCenter}
                >
                    <ModalHeader>
                        타순 설정
                        <CloseButton
                            src={IcClose}
                            onClick={closeModal}
                        />
                    </ModalHeader>

                    <ContentContainer>
                        <BatterLineupConatiner>
                            <HorizontalConatiner>
                                <VerticalContainer>
                                    <RedLineupHeader>상위 타선</RedLineupHeader>
                                    <HorizontalConatiner>
                                        {upperLineup.map((order) => {
                                            return (
                                                <DroppablePlayerCardHolder
                                                    key={order}
                                                    id={`slot-${order}`}
                                                    order={order}
                                                    isForDisplay={true}
                                                    position={playerInfos[order]}
                                                />
                                            )
                                        })}
                                    </HorizontalConatiner>
                                </VerticalContainer>
                                
                                <VerticalContainer>
                                    <BlueLineupHeader>클린업</BlueLineupHeader>
                                    <HorizontalConatiner>
                                        {cleanupLineup.map((order) => {
                                            return (
                                                <DroppablePlayerCardHolder
                                                    key={order}
                                                    id={`slot-${order}`}
                                                    order={order}
                                                    isForDisplay={true}
                                                    position={playerInfos[order]}
                                                />
                                            )
                                        })}
                                    </HorizontalConatiner>
                                </VerticalContainer>
                            </HorizontalConatiner>

                            <VerticalContainer>
                                <GreenLineupHeader>하위 타선</GreenLineupHeader>
                                <HorizontalConatiner>
                                    {lowerLineup.map((order) => {
                                        return (
                                            <DroppablePlayerCardHolder
                                                key={order}
                                                id={`slot-${order}`}
                                                order={order}
                                                isForDisplay={true}
                                                position={playerInfos[order]}
                                            />
                                        )
                                    })}
                                </HorizontalConatiner>
                            </VerticalContainer>
                        </BatterLineupConatiner>

                        <CandidateContainer>
                            
                            {
                                selectionBatterPositionKeys.map((position) => {
                                    const player = playerInfos[position];
                                    
                                    const orders = ['1번', '2번', '3번', '4번', '5번', '6번', '7번', '8번', '9번']
                                    const isPlayerInLineup = orders.map((order) => playerInfos[order]).includes(position);

                                    if (!player || isPlayerInLineup) {
                                        return null;
                                    }
                                    
                                    // 후보 선수 카드: order는 null 또는 undefined로 전달
                                    return (
                                        <DraggablePlayerCard
                                            key={player.id || position}
                                            player={player}
                                            position={position}
                                            // order는 라인업 슬롯에만 필요하므로 여기서는 생략하거나 null 처리 가능
                                            // order={null} 
                                        />
                                    );
                                })
                            }
                        </CandidateContainer>
                    </ContentContainer>
                </DndContext>
            </ModalContainer>
        </Modal>
    )
}